<template>
  <bt-realization-form :value="realization" :title="title" @save="save"
                       :newExpenseRoute="{name: 'business-trip-new-realization-new-expense'}"
                       :expenseDetailRoute="{name: 'business-trip-new-realization-expense-detail'}"
                       @input="change" @close="close" @generateExpense="generateExpense" @updateExpense="updateExpense"
                       cancelable @cancel="cancel" v-if="loaded"/>
  <h2 v-else>Loading...</h2>
</template>

<script>
import Vuex from 'vuex'
import _ from 'lodash'
import Promise from 'promise-polyfill'
import BtRealizationForm from './BtRealizationForm'

const btRequestExpenseStore = Vuex.createNamespacedHelpers('BusinessTripRequest/Expenses')
const btRealizationStore = Vuex.createNamespacedHelpers('BusinessTripRealization/Realization')
const organizationStore = Vuex.createNamespacedHelpers('Organization')
const dialogsStore = Vuex.createNamespacedHelpers('Dialogs')
const tripExpenseStore = Vuex.createNamespacedHelpers('TripExpense')

function propsValid (vm) {
  return !((vm.id === null || vm.empid === null || vm.sppdid === null) &&
  (vm.org === null || vm.sppd === null))
}

export default {
  name: 'BtRealizationNew',
  components: {
    BtRealizationForm
  },
  props: {
    id: {
      type: [String, Number],
      required: false,
      default: null
    },
    empid: {
      type: [String, Number],
      required: false,
      default: null
    },
    sppdid: {
      type: [String, Number],
      required: false,
      default: null
    },
    org: {
      type: Object,
      required: false,
      default: null
    },
    sppd: {
      type: Object,
      required: false,
      default: null
    },
    backRoute: {
      type: [String, Object],
      required: false,
      default: null
    }
  },
  data () {
    return {
      title: 'New Business Trip Realization',
      realization: {},
      loaded: false,
      modified: true
    }
  },
  computed: {
    ...tripExpenseStore.mapState({
      tripExpenseTypes: 'items'
    })
  },
  methods: {
    ...btRequestExpenseStore.mapActions({
      getExpenses: 'getExpenses'
    }),
    ...btRealizationStore.mapActions({
      getNewRealization: 'getNewRealization',
      addRealization: 'addRealization',
      getEmployeeRealizationById: 'getEmployeeRealizationById',
      generateRealizationExpense: 'generateRealizationExpense'
    }),
    ...organizationStore.mapActions({
      getAuthorizedOrgById: 'getAuthorizedOrgById'
    }),
    ...dialogsStore.mapActions({
      showConfirmation: 'showConfirmation',
      closeConfirmation: 'closeConfirmation'
    }),
    ...tripExpenseStore.mapActions({
      listExpenseTypes: 'list'
    }),
    loadResources () {
      const self = this
      self.loaded = false

      if (!propsValid(self)) {
        self.$error('Props not valid, please select valid SPPD before continue')
        self.$router.replace(this.backRoute)
      } else {
        const orgresource = new Promise((resolve, reject) => {
          if (self.org !== null) {
            resolve(self.org)
          } else {
            self.getAuthorizedOrgById({vueinstance: self, id: self.id})
          }
        })

        const sppdresource = new Promise((resolve, reject) => {
          if (self.sppd !== null) {
            resolve(self.sppd)
          } else {
            self.getEmployeeRealizationById({vueinstance: self, id: self.empid, orgid: self.id})
          }
        })

        const resources = [
          orgresource,
          sppdresource,
          self.getNewRealization({vueinstance: self}),
          self.getExpenses({
            vueinstance: self,
            id: self.sppdid || self.sppd.sppd.id,
            empid: self.empid || self.sppd.id
          }),
          self.listExpenseTypes({vueinstance: self})
        ]

        Promise.all(resources)
          .then(res => {
            self.realization = _.cloneDeep(res[2])
            self.realization.hcOrganization = res[0]
            const sppdEmployee = {
              ...res[1],
              displayName: res[1].employee.name + ' (' + res[1].employee.nik + ') - ' + res[1].sppd.batchNo
            }
            self.realization.sppdEmployee = sppdEmployee
            self.realization.fromDate = _.cloneDeep(self.realization.sppdEmployee.fromDate)
            self.realization.toDate = _.cloneDeep(self.realization.sppdEmployee.toDate)

            const expenses = _.cloneDeep(res[3])
              // copy expense included in tripExpenseTypes list
              .filter(x => self.tripExpenseTypes.map(y => y.tripExpenseType).includes(x.tripExpenseType))
            expenses.forEach(x => {
              delete x.id
              x._tag = {status: 'new'}
            })

            self.loaded = true
            self.modified = true
            self._setupToolbar()

            // get generated expense
            self.generateRealizationExpense({vueinstance: self,
              id: self.realization.sppdEmployee.id,
              fromdate: self.realization.fromDate,
              todate: self.realization.toDate,
              fromcity: '-',
              tocity: '-'})
              .then(res1 => {
                res1.forEach(x => {
                  expenses.push({...x, _tag: {status: 'new', generated: true}})

                  self.realization.expenses = expenses
                  self.loaded = true
                  self.modified = true
                  self._setupToolbar()
                })
              }).catch(err => self.$error(err))
          })
          .catch(err => self.$error(err))
      }
    },
    _setupToolbar () {
      this.$store.dispatch('setTitle', {title: this.title})
    },
    close () {
      this.$router.replace(this.backRoute)
    },
    change (value) {
      this.modified = true
      this.realization = value
    },
    save (realization) {
      const self = this

      const simpan = () => {
        // set sppdState
        if (realization.sppdState === null) {
          realization.sppdState = 'REALIZATION'
        }
        this.$store.dispatch('Action/setLoading', true)
        self.addRealization({vueinstance: self, sppdReal: realization})
          .then(res => {
            this.$store.dispatch('Action/setLoading', false)
            self.$success('Business Trip Realization ' + res.batchNo + ' saved')
            self.modified = false
            self.$router.replace(this.backRoute)
          })
          .catch(err => {
            this.$store.dispatch('Action/setLoading', false)
            self.$error(err)
          })
      }

      if (realization.documentState === 'SUBMITTED') {
        self.showConfirmation({
          title: 'Submit Business Trip Realization. This process cannot be undone.',
          message: 'Are you sure?',
          buttons: [
            {
              label: 'Yes',
              handler: () => {
                self.closeConfirmation()
                simpan()
              }
            },
            {label: 'No', handler: () => self.closeConfirmation()}
          ]
        })
      } else {
        simpan()
      }
    },
    generateExpense () {
      const self = this
      const generate = () => {
        var fromCities = '-'
        var toCities = '-'
        if (self.realization.fromLat != null && self.realization.fromLong != null && self.realization.toLat != null && self.realization.toLong != null) {
          fromCities = self.realization.fromLat + ',' + self.realization.fromLong
          toCities = self.realization.toLat + ',' + self.realization.toLong
        }
        self.loaded = false
        const expenses = self.realization.expenses.filter(x => !x._tag.generated)
        self.generateRealizationExpense({
          vueinstance: self,
          id: self.realization.sppdEmployee.id,
          fromdate: self.realization.fromDate,
          todate: self.realization.toDate,
          fromcity: fromCities,
          tocity: toCities
        }).then(res => {
          res.map(x => {
            return {...x, _tag: {status: 'new', generated: true}}
          }).forEach(x => expenses.push(x))
          self.$set(self.realization, 'expenses', Object.assign([], expenses))
          self.loaded = true
          self.modified = true
          self.closeConfirmation()
        }).catch(err => {
          self.$error(err)
          self.closeConfirmation()
        })
      }

      self.showConfirmation({
        title: 'Generate expenses? All previous generated expense(s) will be generated again.',
        message: 'Are you sure?',
        buttons: [
          {label: 'Yes', handler: generate},
          {label: 'No', handler: self.closeConfirmation}
        ]
      })
    },
    updateExpense (expenses) {
      this.$set(this.realization, 'expenses', expenses)
    },
    cancel (realization) {
      const self = this

      const batal = () => {
        realization.sppdState = 'CANCELED'
        realization.documentState = 'LOCKED'
        self.save(realization)
        self.closeConfirmation()
      }

      self.showConfirmation({
        title: 'Cancel Business Trip Realization? This process cannot be undone.',
        message: 'Are you sure?',
        buttons: [
          {label: 'Yes', handler: batal},
          {label: 'No', handler: () => self.closeConfirmation()}
        ]
      })
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => vm.loadResources())
  },
  beforeRouteLeave (to, from, next) {
    const yesHandler = () => {
      this.closeConfirmation()
      next()
    }

    const noHandler = () => {
      this.closeConfirmation()
    }

    if (this.modified) {
      this.showConfirmation({
        message: 'Are you sure?',
        title: 'This Business Trip Expense Realization will not saved.',
        buttons: [{label: 'Yes', handler: yesHandler}, {label: 'No', handler: noHandler}]
      })
    } else {
      next()
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/bt-realization/BtRealizationNew.vue